/**
 * Create Driver Modal Component
 * Form for creating a new driver
 */

import { useState, useEffect } from 'react';
import { Loader2 } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../ui/dialog';
import { Button } from '../../ui/button';
import { useCreateDriver } from '../../../hooks/useDrivers';
import { createDriverSchema, cdlClasses, daysOfWeek } from '../../../schemas/driverSchema';
import type { CreateDriverFormData } from '../../../schemas/driverSchema';
import { ApiError } from '../../../services/api';

interface CreateDriverModalProps {
  open: boolean;
  onClose: () => void;
}

export function CreateDriverModal({ open, onClose }: CreateDriverModalProps) {
  const { mutateAsync: createDriver, isPending } = useCreateDriver();
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [formData, setFormData] = useState<CreateDriverFormData>({
    employeeNumber: '',
    firstName: '',
    lastName: '',
    phone: '',
    email: '',
    cdlNumber: '',
    cdlClass: 'B',
    cdlExpirationDate: '',
    endorsements: [],
    medicalCertExpiration: '',
    hireDate: new Date().toISOString().split('T')[0] as string,
    availabilityDays: ['monday', 'tuesday', 'wednesday', 'thursday', 'friday'],
    notes: '',
  });

  useEffect(() => {
    if (open) {
      setFormData({
        employeeNumber: '',
        firstName: '',
        lastName: '',
        phone: '',
        email: '',
        cdlNumber: '',
        cdlClass: 'B',
        cdlExpirationDate: '',
        endorsements: [],
        medicalCertExpiration: '',
        hireDate: new Date().toISOString().split('T')[0] as string,
        availabilityDays: ['monday', 'tuesday', 'wednesday', 'thursday', 'friday'],
        notes: '',
      });
      setErrors({});
    }
  }, [open]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setErrors({});

    const result = createDriverSchema.safeParse(formData);
    if (!result.success) {
      const newErrors: Record<string, string> = {};
      result.error.errors.forEach((err) => {
        if (err.path[0]) newErrors[String(err.path[0])] = err.message;
      });
      setErrors(newErrors);
      return;
    }

    try {
      await createDriver(result.data);
      onClose();
    } catch (error) {
      // Handle API validation errors with field-level details
      if (error instanceof ApiError && error.details && error.details.length > 0) {
        const fieldErrors: Record<string, string> = {};
        error.details.forEach((detail) => {
          fieldErrors[detail.field] = detail.message;
        });
        setErrors(fieldErrors);
      } else {
        // Fallback to generic error message
        setErrors({ submit: error instanceof Error ? error.message : 'Failed to create driver' });
      }
    }
  };

  const updateField = (
    field: keyof CreateDriverFormData,
    value: string | string[] | number
  ) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
  };

  const toggleDay = (day: (typeof daysOfWeek)[number]) => {
    const newDays = formData.availabilityDays.includes(day)
      ? formData.availabilityDays.filter((d) => d !== day)
      : [...formData.availabilityDays, day];
    updateField('availabilityDays', newDays);
  };

  return (
    <Dialog open={open} onOpenChange={(isOpen) => !isOpen && onClose()}>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle>Add New Driver</DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Employee Number */}
          <div>
            <label className="block text-sm font-medium text-gray-700">
              Employee Number <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={formData.employeeNumber}
              onChange={(e) => updateField('employeeNumber', e.target.value.toUpperCase())}
              className={`mt-1 block w-full rounded-md border ${
                errors.employeeNumber ? 'border-red-500' : 'border-gray-300'
              } px-3 py-2 focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500`}
              placeholder="EMP-001"
            />
            {errors.employeeNumber && <p className="mt-1 text-sm text-red-500">{errors.employeeNumber}</p>}
          </div>

          {/* Name */}
          <div className="grid gap-4 sm:grid-cols-2">
            <div>
              <label className="block text-sm font-medium text-gray-700">
                First Name <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={formData.firstName}
                onChange={(e) => updateField('firstName', e.target.value)}
                className={`mt-1 block w-full rounded-md border ${
                  errors.firstName ? 'border-red-500' : 'border-gray-300'
                } px-3 py-2 focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500`}
              />
              {errors.firstName && <p className="mt-1 text-sm text-red-500">{errors.firstName}</p>}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700">
                Last Name <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={formData.lastName}
                onChange={(e) => updateField('lastName', e.target.value)}
                className={`mt-1 block w-full rounded-md border ${
                  errors.lastName ? 'border-red-500' : 'border-gray-300'
                } px-3 py-2 focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500`}
              />
              {errors.lastName && <p className="mt-1 text-sm text-red-500">{errors.lastName}</p>}
            </div>
          </div>

          {/* Contact */}
          <div className="grid gap-4 sm:grid-cols-2">
            <div>
              <label className="block text-sm font-medium text-gray-700">
                Phone <span className="text-red-500">*</span>
              </label>
              <input
                type="tel"
                value={formData.phone}
                onChange={(e) => updateField('phone', e.target.value)}
                className={`mt-1 block w-full rounded-md border ${
                  errors.phone ? 'border-red-500' : 'border-gray-300'
                } px-3 py-2 focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500`}
                placeholder="(805) 555-1234"
              />
              {errors.phone && <p className="mt-1 text-sm text-red-500">{errors.phone}</p>}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700">Email</label>
              <input
                type="email"
                value={formData.email}
                onChange={(e) => updateField('email', e.target.value)}
                className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
              />
            </div>
          </div>

          {/* CDL Info */}
          <div className="grid gap-4 sm:grid-cols-2">
            <div>
              <label className="block text-sm font-medium text-gray-700">
                CDL Number <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={formData.cdlNumber}
                onChange={(e) => updateField('cdlNumber', e.target.value.toUpperCase())}
                className={`mt-1 block w-full rounded-md border ${
                  errors.cdlNumber ? 'border-red-500' : 'border-gray-300'
                } px-3 py-2 focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500`}
              />
              {errors.cdlNumber && <p className="mt-1 text-sm text-red-500">{errors.cdlNumber}</p>}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700">
                CDL Class <span className="text-red-500">*</span>
              </label>
              <select
                value={formData.cdlClass}
                onChange={(e) => updateField('cdlClass', e.target.value)}
                className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
              >
                {cdlClasses.map((cls) => (
                  <option key={cls} value={cls}>Class {cls}</option>
                ))}
              </select>
            </div>
          </div>

          {/* CDL & Medical Expiration */}
          <div className="grid gap-4 sm:grid-cols-2">
            <div>
              <label className="block text-sm font-medium text-gray-700">
                CDL Expiration <span className="text-red-500">*</span>
              </label>
              <input
                type="date"
                value={formData.cdlExpirationDate}
                onChange={(e) => updateField('cdlExpirationDate', e.target.value)}
                className={`mt-1 block w-full rounded-md border ${
                  errors.cdlExpirationDate ? 'border-red-500' : 'border-gray-300'
                } px-3 py-2 focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500`}
              />
              {errors.cdlExpirationDate && <p className="mt-1 text-sm text-red-500">{errors.cdlExpirationDate}</p>}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700">Medical Cert Expiration</label>
              <input
                type="date"
                value={formData.medicalCertExpiration}
                onChange={(e) => updateField('medicalCertExpiration', e.target.value)}
                className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
              />
            </div>
          </div>

          {/* Hire Date */}
          <div>
            <label className="block text-sm font-medium text-gray-700">
              Hire Date <span className="text-red-500">*</span>
            </label>
            <input
              type="date"
              value={formData.hireDate}
              onChange={(e) => updateField('hireDate', e.target.value)}
              className={`mt-1 block w-full rounded-md border ${
                errors.hireDate ? 'border-red-500' : 'border-gray-300'
              } px-3 py-2 focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500`}
            />
            {errors.hireDate && <p className="mt-1 text-sm text-red-500">{errors.hireDate}</p>}
          </div>

          {/* Availability Days */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Available Days <span className="text-red-500">*</span>
            </label>
            <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
              {daysOfWeek.map((day) => (
                <label key={day} className="flex items-center gap-2 rounded-md border border-gray-300 p-2 cursor-pointer hover:bg-gray-50">
                  <input
                    type="checkbox"
                    checked={formData.availabilityDays.includes(day)}
                    onChange={() => toggleDay(day)}
                    className="rounded"
                  />
                  <span className="text-sm capitalize">{day}</span>
                </label>
              ))}
            </div>
            {errors.availabilityDays && <p className="mt-1 text-sm text-red-500">{errors.availabilityDays}</p>}
          </div>

          {/* Notes */}
          <div>
            <label className="block text-sm font-medium text-gray-700">Notes</label>
            <textarea
              value={formData.notes}
              onChange={(e) => updateField('notes', e.target.value)}
              rows={3}
              className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
            />
          </div>

          {errors.submit && (
            <div className="rounded-md bg-red-50 p-3 text-sm text-red-700">{errors.submit}</div>
          )}

          <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
            <Button type="button" variant="outline" onClick={onClose} disabled={isPending}>
              Cancel
            </Button>
            <Button type="submit" disabled={isPending}>
              {isPending ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Creating...
                </>
              ) : (
                'Create Driver'
              )}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
